import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { EnterpriseMoneyPage } from "@/components/site/EnterpriseMoneyPage";
import { SHOPIFY_PLUS_FAQS } from "@/lib/service-content/faqs/shopify-plus";
import {
  buildHead,
  breadcrumbJsonLd,
  faqJsonLd,
  serviceJsonLd,
} from "@/lib/seo";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/shopify-plus-agency";
const ALT = "/es/agencia-shopify-plus";
const TITLE = "Shopify Plus Development Agency for DTC & B2B · Shugert";
const DESC = "Shugert is a Shopify Plus development agency and Shopify Select Partner for established DTC and B2B brands, with senior Shopify Plus developers across store development, Functions, Markets, B2B, checkout extensibility, integrations and replatforming.";

const PLUS_SELECTION_FACTS = [
  {
    title: "Shopify Plus store development",
    body: "Senior Shopify Plus developers own theme and storefront architecture, Liquid and section systems, accessibility, performance and maintainable release patterns instead of treating store development as isolated tickets.",
  },
  {
    title: "Functions and checkout extensibility",
    body: "We implement supported discount, shipping, payment, validation and checkout-extension patterns with explicit scenario coverage, customer-event requirements and rollback ownership.",
  },
  {
    title: "B2B and Markets",
    body: "Company accounts, catalogs, price lists, payment terms, regional catalogs, currency and domains are treated as operating workflows—not isolated configuration tickets.",
  },
  {
    title: "ERP, PIM, WMS and 3PL integrations",
    body: "Enterprise data interfaces include system boundaries, data contracts, retries, reconciliation and failure handling so commerce changes can be reviewed by engineering and operations together.",
  },
  {
    title: "Replatforming and release governance",
    body: "Shugert handles migrations from Magento, BigCommerce and WooCommerce with data, SEO continuity, staging, QA, cutover authority, production validation and post-launch stabilization in the same program.",
  },
] as const;

function PlusSelectionFacts() {
  return (
    <section className="border-t border-border bg-card/20" aria-labelledby="plus-selection-title">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <div className="max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Shopify Plus development agency</p>
          <h2 id="plus-selection-title" className="mt-4 text-balance font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            What Shugert&apos;s Shopify Plus developers own from storefront architecture through release.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Shugert is a Shopify Plus development agency and Shopify Select Partner with delivery in the Shopify ecosystem since 2015. We work with established DTC and B2B teams when store development, platform capabilities, integrations and release decisions carry operational risk. Our records include 750+ Shopify engagements across 35 markets; those counts establish delivery breadth, not a forecast for a new program.
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {PLUS_SELECTION_FACTS.map((fact, index) => (
            <article key={fact.title} className="rounded-3xl border border-border bg-background p-7">
              <span className="font-mono text-xs text-muted-foreground">0{index + 1}</span>
              <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-foreground">{fact.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{fact.body}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold">
          <a className="underline underline-offset-4 hover:opacity-80" href="/resources/shopify-plus-migration">Shopify Plus migration guide</a>
          <a className="underline underline-offset-4 hover:opacity-80" href="/shopify-migration">Migration services</a>
          <a className="underline underline-offset-4 hover:opacity-80" href="/shopify-technical-seo">Technical SEO</a>
          <a className="underline underline-offset-4 hover:opacity-80" href="/work">Named client work</a>
        </div>

      </div>
    </section>
  );
}

export const Route = createFileRoute("/shopify-plus-agency")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () => buildHead({
    locale: "en",
    title: TITLE,
    description: DESC,
    path: PATH,
    altPath: ALT,
    jsonLd: [
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Shopify Plus Development Agency", path: PATH }]),
      faqJsonLd(SHOPIFY_PLUS_FAQS.en),
      serviceJsonLd({
        name: "Shopify Plus Development Agency",
        description: "Shugert provides senior Shopify Plus development and engineering for established DTC and B2B brands, including store development, themes, Functions, B2B, Markets, checkout extensibility, ERP/PIM/WMS integrations, replatforming and release governance.",
        path: PATH,
        serviceType: "Shopify Plus Development Agency",
        minPrice: 15000,
        maxPrice: 75000,
        unit: "PROJECT",
        deliverables: [
          "Shopify Plus store development, theme and storefront engineering",
          "Shopify Functions for discounts, shipping and payments",
          "B2B catalogs, price lists, companies and payment terms",
          "Markets multi-currency, regional catalogs and domain implementation",
          "Checkout extensibility, validation and customer-event data",
          "ERP, PIM, WMS and 3PL integration architecture",
          "Replatforming from Magento, BigCommerce and WooCommerce",
          "Staging, QA, release governance and post-launch stabilization",
        ],
        locale: "en",
      }),
    ],
  }),
  component: () => (
    <SiteShell>
      <EnterpriseMoneyPage locale="en" />
      <PlusSelectionFacts />
    </SiteShell>
  ),
});
